import { useRef } from "react";
import { focusRingInsetClass } from "@/components/ui";
import { cn } from "@/lib/utils";
import { COLUMN_RESIZE_KEYBOARD_STEP_PX } from "./tableConstants";

interface ColumnResizeHandleProps {
  ariaLabel: string;
  ariaValueNow: number;
  ariaValueMin: number;
  ariaValueMax: number;
  onResizeStart: (
    clientX: number,
    headerElement: HTMLElement,
    handleElement: HTMLElement,
    pointerId: number,
  ) => void;
  onKeyboardResize: (deltaPx: number, headerElement: HTMLElement) => void;
}

export function ColumnResizeHandle({
  ariaLabel,
  ariaValueNow,
  ariaValueMin,
  ariaValueMax,
  onResizeStart,
  onKeyboardResize,
}: ColumnResizeHandleProps) {
  const handleRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={ariaValueNow}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      tabIndex={0}
      onPointerDown={(event) => {
        if (event.button !== 0) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const headerElement = event.currentTarget.parentElement;
        if (!(headerElement instanceof HTMLElement)) {
          return;
        }
        onResizeStart(
          event.clientX,
          headerElement,
          event.currentTarget,
          event.pointerId,
        );
      }}
      onKeyDown={(event) => {
        const headerElement = handleRef.current?.parentElement;
        if (!(headerElement instanceof HTMLElement)) {
          return;
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onKeyboardResize(-COLUMN_RESIZE_KEYBOARD_STEP_PX, headerElement);
          return;
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          onKeyboardResize(COLUMN_RESIZE_KEYBOARD_STEP_PX, headerElement);
        }
      }}
      className={cn(
        "absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize",
        "hover:bg-primary-400/60 active:bg-primary-500",
        "focus:outline-none focus-visible:bg-primary-400/60",
        focusRingInsetClass,
      )}
    />
  );
}
